import React, { createContext, useContext, useMemo } from 'react';
import { DateBoundFn } from './filterTypes';

/**
 * The tenant params the generated request functions destructure. Restaurant-scoped
 * functions take {orgSlug, restaurantId}; branch-scoped ones also take branchId. One
 * shape serves both because the generated functions ignore keys they do not read.
 */
export type TenantParams = { orgSlug: string; restaurantId: string; branchId?: string };

/**
 * The URL binding, supplied by the app rather than imported. Keeping react-router out of
 * this package means its tests need no MemoryRouter and a future consumer on a different
 * router needs no fork. The setter takes a concrete URLSearchParams, not a functional
 * update, because react-router-dom@6.0.0 cannot be relied on for the functional form —
 * so a caller must never write twice in one event handler.
 */
export type CrudUrlStateBinding = [URLSearchParams, (next: URLSearchParams) => void];

/**
 * Everything this package needs from the portal. A package cannot import the portal's
 * generated API client, tenant context or i18n, so the dependency is these named
 * functions and nothing else.
 */
export interface CrudConfig {
  useTenantParams: () => TenantParams;
  translate: (key: string, vars?: Record<string, unknown>) => string;
  resolveErrorMessage: (error: unknown) => string | null;
  useUrlState: () => CrudUrlStateBinding;
  /**
   * REQUIRED — a Hook, like useTenantParams: the currency is the current restaurant's, which
   * only exists inside the portal's tenant context, while the provider mounts above it. Called
   * only while a CRUD view renders. The null guard lives in the adapter (defect class #5).
   */
  useFormatCurrency: () => (value: number | null | undefined) => string;
  formatDate: (value: string | Date | null | undefined, opts?: { relative?: boolean }) => string;
  /**
   * REQUIRED — the package cannot do locale day math (spec §7.2's date note), so the date
   * filters' `on`/`before`/`after` bounds are injected. Returning null drops the chip.
   */
  dateBound: DateBoundFn;
  /** BCP-47 tag the adapter renders in, for date formatting and locale-aware display. */
  locale: string;
}

const CrudConfigContext = createContext<CrudConfig | null>(null);

export const CrudConfigProvider = ({
  useTenantParams,
  translate,
  resolveErrorMessage,
  useUrlState,
  useFormatCurrency,
  formatDate,
  dateBound,
  locale,
  children,
}: CrudConfig & { children: React.ReactNode }) => {
  const value = useMemo(
    () => ({
      useTenantParams,
      translate,
      resolveErrorMessage,
      useUrlState,
      useFormatCurrency,
      formatDate,
      dateBound,
      locale,
    }),
    [useTenantParams, translate, resolveErrorMessage, useUrlState, useFormatCurrency, formatDate, dateBound, locale]
  );
  return <CrudConfigContext.Provider value={value}>{children}</CrudConfigContext.Provider>;
};

export function useCrudConfig(): CrudConfig {
  const config = useContext(CrudConfigContext);
  if (!config) {
    throw new Error('useCrudConfig must be used inside <CrudConfigProvider>. Mount it once at the app root.');
  }
  return config;
}
